import React from "react";
import { TATVA_PHOTO_WYNN_B64 } from "../lib/tatvaPhotoWynn";

/**
 * 2-page A4 Resume — Wynn Al Marjan Island
 * Production Manager · Audio Visual (Job ID 1157)
 *
 * Content: 20+ years live AV production, broadcast, sound engineering.
 * Tone: factual, professional, no flourish.
 *
 * Uses same data-testids as Resume.jsx (resume-page-1, resume-page-2)
 * so the existing /api/application-wynn/pdf endpoint picks it up.
 */

const CREAM = "#f4ede0";
const INK = "#1a1a1a";
const INK_SOFT = "#4a4338";
const BRONZE = "#8a6f47";
const RULE = "rgba(26,26,26,0.18)";
const RULE_SOFT = "rgba(26,26,26,0.10)";

const SERIF = '"Playfair Display", Georgia, "Times New Roman", serif';
const SANS = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";

/* ─────────── atoms ─────────── */

const Eyebrow = ({ children }) => (
    <p
        style={{
            fontFamily: SANS,
            color: BRONZE,
            fontSize: 9,
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            fontWeight: 600,
            margin: 0,
        }}
    >
        {children}
    </p>
);

const SectionLabel = ({ num, label }) => (
    <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontFamily: SERIF, color: BRONZE, fontSize: 14, fontStyle: "italic", fontWeight: 500 }}>
            {num}
        </span>
        <span style={{ flex: 1, height: 1, backgroundColor: RULE }} />
        <span style={{ fontFamily: SANS, color: INK, fontSize: 9, letterSpacing: "0.36em", textTransform: "uppercase", fontWeight: 700 }}>
            {label}
        </span>
    </div>
);

const Entry = ({ role, employer, years, bullets }) => (
    <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
            <p style={{ fontFamily: SERIF, fontSize: 14, fontWeight: 600, color: INK, margin: 0, lineHeight: 1.2 }}>
                {role}
                <span style={{ fontWeight: 400, color: BRONZE, fontStyle: "italic" }}> · {employer}</span>
            </p>
            <p style={{ fontFamily: SANS, fontSize: 9, color: INK_SOFT, margin: 0, letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
                {years}
            </p>
        </div>
        <ul style={{ margin: "4px 0 0", padding: "0 0 0 16px", color: INK_SOFT, fontFamily: SANS, fontSize: 10, lineHeight: 1.55 }}>
            {bullets.map((b, i) => (
                <li key={i} style={{ marginBottom: 2 }}>{b}</li>
            ))}
        </ul>
    </div>
);

/* ─────────── PAGE ONE ─────────── */

export const ResumeWynnPageOne = () => (
    <article
        data-testid="resume-page-1"
        style={{
            width: 794,
            height: 1120,
            maxWidth: "100%",
            margin: "20px auto 0",
            backgroundColor: CREAM,
            color: INK,
            boxShadow: "0 24px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(26,26,26,0.06)",
            padding: "44px 64px 36px",
            boxSizing: "border-box",
            fontFamily: SANS,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}
    >
        {/* HEADER */}
        <header style={{ paddingBottom: 14, borderBottom: `1px solid ${RULE_SOFT}`, marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24 }}>
                <div style={{ flex: 1 }}>
                    <h1 style={{ fontFamily: SERIF, fontSize: 34, fontWeight: 500, color: INK, margin: 0, letterSpacing: "-0.02em", lineHeight: 1 }}>
                        Aditya Anand
                    </h1>
                    <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 12, color: BRONZE, margin: "4px 0 0" }}>
                        Known professionally as Tatva K
                    </p>
                    <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 12, color: INK_SOFT, margin: "8px 0 0" }}>
                        Live AV Production &middot; Sound Engineer &middot; Broadcast Producer
                    </p>
                    <p style={{ fontFamily: SANS, fontSize: 9.5, color: INK_SOFT, margin: "10px 0 0", letterSpacing: "0.04em", lineHeight: 1.55 }}>
                        Al Hamra Village, Ras Al Khaimah, UAE
                        <br />
                        tatvak@theresetsanctuary.org &middot; +971 50 676 9007
                    </p>
                </div>
                <img
                    src={TATVA_PHOTO_WYNN_B64}
                    alt="Tatva K"
                    style={{
                        width: 110,
                        height: 130,
                        objectFit: "cover",
                        border: `1px solid ${RULE}`,
                        flexShrink: 0,
                    }}
                />
            </div>
        </header>

        {/* SUMMARY */}
        <p style={{ fontFamily: SERIF, fontSize: 12, color: INK_SOFT, lineHeight: 1.55, margin: "0 0 18px" }}>
            Twenty years across live performance, broadcast and sound engineering. Sunburn Festival mainstage performer
            (2006&ndash;2009), Producer / DJ / RJ on <em style={{ color: BRONZE }}>Mocktail</em> at Zabardast Hit 95&nbsp;FM
            for thirteen years, Berklee-trained in Professional Music and Advanced Production. Engineered and mastered
            commercial releases totalling <strong style={{ color: INK }}>200M+ streams</strong>. UAE resident, available
            for on-site work in Ras Al Khaimah.
        </p>

        {/* TOOLS STRIP */}
        <div style={{ marginBottom: 14, padding: "12px 14px", border: `1px solid ${RULE_SOFT}`, backgroundColor: "rgba(138,111,71,0.04)" }}>
            <Eyebrow>Studio &amp; Live Rig</Eyebrow>
            <p style={{ fontFamily: SANS, fontSize: 10, color: INK_SOFT, margin: "6px 0 0", lineHeight: 1.6 }}>
                Antelope Audio Orion 32 (AD/DA) &middot; SSL X-Rack preamps &middot; TLA Ivory &middot; SPL 5.1 surround
                &middot; TC Electronic Finalizer (mastering) &middot; Ableton Live (since 2004) &middot; Ableton Push
                &middot; ROLI Seaboard &middot; Pioneer CDJs &middot; full DJ rig
            </p>
        </div>

        {/* AUDIENCE STRIP */}
        <div style={{ marginBottom: 22, padding: "12px 14px", border: `1px solid ${RULE_SOFT}`, backgroundColor: "rgba(138,111,71,0.04)" }}>
            <Eyebrow>Distribution &amp; Reach</Eyebrow>
            <p style={{ fontFamily: SANS, fontSize: 10, color: INK_SOFT, margin: "6px 0 0", lineHeight: 1.6 }}>
                200M+ catalogue streams &middot; 385K+ YouTube subscribers across three properties (Good Vibes
                Entertainment, Chill Om, Tatva K Official) &middot; 130K+ Instagram followers &middot; Verified Teacher
                on Insight Timer (30M+ users) &middot; iTunes #11 collaboration (Juggy D) &middot; BBC UK feature with
                Bobby Friction &middot; Rolling Stone &amp; Verve coverage
            </p>
        </div>

        {/* SECTION 01 — LIVE PERFORMANCE */}
        <SectionLabel num="01" label="Live Performance" />
        <Entry
            role="Mainstage Performer"
            employer="Sunburn Festival, India"
            years="2006 — 2009"
            bullets={[
                "Performed four consecutive years on the Sunburn Festival mainstage alongside Armin van Buuren and Defected Records artists.",
                "Asia's largest electronic music festival; audiences 50,000+ per night.",
                "Live performance under broadcast-grade AV conditions: front-of-house mix, in-ear monitoring, video sync, multi-zone PA coordination.",
            ]}
        />
        <Entry
            role="Resident / Touring DJ &amp; Live Performer"
            employer="India national circuit"
            years="2004 — present"
            bullets={[
                "Headline sets at Plethora, Elevate, MTV Flyp Delhi, Playboy Club, and the wider EDM/superclub circuit.",
                "High-end private events: Mehrangarh Fort weddings (Jodhpur), Udaipur Palace weddings, and multiple destination productions for HNWI clientele.",
                "Self-produced live sets using Ableton Live, Push, ROLI Seaboard and CDJ hybrid setups.",
            ]}
        />

        {/* SECTION 02 — BROADCAST */}
        <SectionLabel num="02" label="Broadcast" />
        <Entry
            role="Producer / DJ / RJ — &lsquo;Mocktail&rsquo;"
            employer="Zabardast Hit 95 FM"
            years="2013 — 2026"
            bullets={[
                "Thirteen-year tenure across triple role: producing, presenting and live mixing a flagship national radio show.",
                "Daily mission-critical broadcast operation: live-to-air signal flow, studio AV uptime, talent and guest coordination.",
                "Built recurring on-air formats, music programming and brand integrations for national reach.",
            ]}
        />
    </article>
);

/* ─────────── PAGE TWO ─────────── */

export const ResumeWynnPageTwo = () => (
    <article
        data-testid="resume-page-2"
        style={{
            width: 794,
            height: 1120,
            maxWidth: "100%",
            margin: "20px auto 0",
            backgroundColor: CREAM,
            color: INK,
            boxShadow: "0 24px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(26,26,26,0.06)",
            padding: "44px 64px 36px",
            boxSizing: "border-box",
            fontFamily: SANS,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}
    >
        {/* HEADER (continuation) */}
        <header style={{ paddingBottom: 12, borderBottom: `1px solid ${RULE_SOFT}`, marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div>
                <p style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 500, color: INK, margin: 0, letterSpacing: "-0.01em" }}>
                    Aditya Anand <span style={{ color: BRONZE, fontStyle: "italic", fontWeight: 400 }}>&middot; Tatva K</span>
                </p>
                <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 11, color: BRONZE, margin: "2px 0 0" }}>
                    Continued &middot; page two
                </p>
            </div>
            <Eyebrow>Production &middot; Engineering &middot; Leadership</Eyebrow>
        </header>

        {/* SECTION 03 — SOUND ENGINEERING / PRODUCTION */}
        <SectionLabel num="03" label="Sound Engineering &amp; Music Production" />
        <Entry
            role="Sound Engineer &amp; Music Producer"
            employer="Independent, India / UAE"
            years="2004 — present"
            bullets={[
                "Trained sound engineer; Ableton Live user since 2004 (22+ years on the DAW).",
                "Engineered and produced multiple commercial hit releases across the Indian market; iTunes #11 chart entry on Juggy D collaboration.",
                "Mixed and mastered catalogue achieving 200M+ combined streams and views; scaled independent label to a Tier-1 commercial deal with Times Music India.",
                "Built and operates audience network: 385K+ YouTube subscribers across three properties, 130K+ Instagram followers, Verified Teacher on Insight Timer (30M+ user platform).",
                "Production range covers electronic dance, broadcast jingles, film &amp; brand work, and ambient compositions.",
            ]}
        />

        {/* SECTION 04 — AI & SYSTEMS */}
        <SectionLabel num="04" label="AI &amp; Systems Fluency" />
            <Entry
                role="Applied AI &amp; Full-Stack Operator"
                employer="Self-directed, 2021 — present"
                years=""
                bullets={[
                    "Daily AI video stack: Kaiber AI (primary), Runway, Pika, MidJourney. Daily generative pipeline: Claude and Emergent APIs for prompt logic, ElevenLabs for voice synthesis.",
                    "Self-edits long-form video, music videos, social reels and ad creatives in Final Cut Pro X. All Reset Sanctuary and Reel Business Inc. ad creatives, reels and music video work produced and edited in-house.",
                    "Built and operates a production FastAPI backend with Meta Conversions API, server-side Stripe webhooks, dedup'd event pipelines and CRM automation.",
                    "Comfortable bridging traditional AV signal flow with modern AI-driven content, automation and show-control workflows.",
                ]}
            />

        {/* SECTION 05 — LEADERSHIP */}
        <SectionLabel num="05" label="Founder &amp; Leadership Roles" />
        <Entry
            role="Founder"
            employer="The Reset Sanctuary"
            years="2021 — present"
            bullets={[
                "Founded and operate a UAE-based live sound and broadcast studio serving global clients.",
                "Architect the full operational stack end-to-end: live session delivery, broadcast infrastructure, payment and CRM systems.",
            ]}
        />
        <Entry
            role="Founder &amp; Managing Director"
            employer="Chill Om Records &amp; Studios"
            years="2004 — 2013"
            bullets={[
                "Founded independent record label and recording studio in India.",
                "Scaled to commercial distribution partnership with Times Music India.",
                "Managed studio operations, artist roster and production pipeline.",
            ]}
        />
        <Entry
            role="Founder"
            employer="Reel Business Inc."
            years="2021 — 2022"
            bullets={[
                "Built end-to-end AI audiobook narration and distribution pipeline for international authors.",
            ]}
        />

        {/* SECTION 06 — EDUCATION */}
        <SectionLabel num="06" label="Education &amp; Certifications" />
        <Entry
            role="Bachelor of Arts &mdash; History"
            employer="Hindu College, University of Delhi"
            years=""
            bullets={[
                "Three-year undergraduate degree from one of India's most reputed liberal arts institutions.",
            ]}
        />
        <Entry
            role="Berklee College of Music"
            employer="Music Therapy Masterclass"
            years=""
            bullets={[
                "Formal coursework at Berklee covering the science of sound, music and the nervous system, with applied production technique.",
            ]}
        />
        <Entry
            role="IPHM"
            employer="International Practitioners of Holistic Medicine"
            years=""
            bullets={[
                "Reiki Grand Master certification.",
            ]}
        />

        {/* SECTION 07 — SPEAKING / MEDIA */}
        <SectionLabel num="07" label="Speaking &amp; Media" />
        <Entry
            role="TEDx Speaker"
            employer="Mainstage talk on sound and cognition"
            years="2012"
            bullets={[
                "Invited speaker on the science of sound, cognition and subconscious response.",
            ]}
        />
        <Entry
            role="Media Coverage"
            employer="BBC, Rolling Stone India, Verve, national press"
            years="ongoing"
            bullets={[
                "Featured on BBC Asian Network with Bobby Friction (Pathaan's Global Musical Rickshaw); ongoing coverage across Rolling Stone India, Verve and national music press.",
            ]}
        />

        {/* FOOTER */}
        <div style={{ marginTop: "auto", paddingTop: 16, borderTop: `1px solid ${RULE_SOFT}`, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 12, color: INK_SOFT, margin: 0 }}>
                References &amp; full production catalogue available on request.
            </p>
            <Eyebrow>UAE Resident &middot; On-site Ready</Eyebrow>
        </div>
    </article>
);
